/**
 * 리포트 댓글 좋아요 추적 훅
 * 
 * 리포트 상세 페이지에서 댓글에 좋아요를 누르는 기능
 * - start()는 세션 스토리지를 확인하여 저장된 의도가 있으면 자동 복원
 * - 로그인 후 리다이렉트되어도 같은 의도 유지
 * - 로그인 의도는 자동으로 부모 의도 연결 (parentIntentType)
 * 
 * @example
 * ```tsx
 * function CommentItem({ reportId, commentId }) {
 *   const commentLike = useReportCommentLike({ 
 *     report_id: reportId, 
 *     comment_id: commentId,
 *     is_like: false 
 *   });
 *   const loginIntent = useLoginIntent();
 *   const isLoggedIn = useAuth().isLoggedIn;
 *   
 *   const handleLike = async (isLike: boolean) => {
 *     commentLike.start({ is_like: isLike }); // 의도 시작
 *     
 *     if (!isLoggedIn) {
 *       // 로그인 의도 시작 (부모 의도 자동 연결)
 *       loginIntent.start(
 *         { redirectUrl: router.asPath },
 *         { parentIntentType: 'report_comment_like' }  // 👈 타입으로 부모 의도 자동 조회
 *       );
 *       router.push('/login');
 *       return;
 *     }
 *     
 *     try {
 *       await apiLikeComment(reportId, commentId, isLike);
 *       commentLike.success();
 *     } catch (error) {
 *       commentLike.fail('backend_5xx');
 *     }
 *   };
 *   
 *   return <button onClick={() => handleLike(true)}>좋아요</button>;
 * }
 * ```
 */

import { useCallback } from 'react';
import { track } from '../../../../core/client';
import { useIntent } from '../useIntent';

export interface ReportCommentLikeContext {
    report_id: number;
    comment_id: number;
    is_like: boolean;
}

export interface ReportCommentLikeActions {
    start: (context?: ReportCommentLikeContext) => void;
    success: () => void;
    fail: (reason?: string) => void;
}

/**
 * 리포트 댓글 좋아요 추적 훅
 */
export function useReportCommentLike(
    initialContext?: ReportCommentLikeContext
): ReportCommentLikeActions {
    const { report_id, comment_id, is_like } = initialContext || {};

    const intent = useIntent<ReportCommentLikeContext>({
        type: 'report_comment_like',
        requiredGate: 'login',
        onStart: ({ intent_id, context, parent_intent_id, required_gate }) => {
            track('report_comment_like_start', {
                intent_id,
                report_id: context?.report_id || report_id || 0,
                comment_id: context?.comment_id || comment_id || 0,
                is_like: context?.is_like !== undefined ? context.is_like : is_like || false,
                parent_intent_id,
                required_gate,
            });
        },
        onSuccess: ({ intent_id, context, latency_ms }) => {
            track('report_comment_like_success', {
                intent_id,
                report_id: context?.report_id || report_id || 0,
                comment_id: context?.comment_id || comment_id || 0,
                is_like: context?.is_like !== undefined ? context.is_like : is_like || false,
                latency_ms,
            });
        },
        onFail: ({ intent_id, context, fail_reason }) => {
            track('report_comment_like_fail', {
                intent_id,
                report_id: context?.report_id || report_id || 0,
                comment_id: context?.comment_id || comment_id || 0,
                is_like: context?.is_like !== undefined ? context.is_like : is_like || false,
                fail_reason,
            });
        },
    });

    /**
     * 댓글 좋아요 의도 시작
     */
    const start = useCallback((context?: ReportCommentLikeContext) => {
        intent.start(context || initialContext);
    }, [intent, initialContext]);

    /**
     * 댓글 좋아요 성공
     */
    const success = useCallback(() => {
        intent.success();
    }, [intent]);

    /**
     * 댓글 좋아요 실패
     */
    const fail = useCallback((reason?: string) => {
        intent.fail(reason);
    }, [intent]);

    return {
        start,
        success,
        fail,
    };
}

